import { LineChart, Table as TableIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ChartGrouping } from "@/hooks/use-auto-chart-interval";

interface DrilldownChartControlsProps {
  grouping: ChartGrouping;
  onGroupingChange: (grouping: ChartGrouping) => void;
  cumulative: boolean;
  onCumulativeChange: (next: boolean) => void;
  viewMode: "table" | "chart";
  onViewModeChange: (next: "table" | "chart") => void;
  showChartControls: boolean;
  showChartToggle: boolean;
}

export function DrilldownChartControls({
  grouping,
  onGroupingChange,
  cumulative,
  onCumulativeChange,
  viewMode,
  onViewModeChange,
  showChartControls,
  showChartToggle,
}: DrilldownChartControlsProps) {
  return (
    <>
      {showChartControls && (
        <>
          <Select
            value={grouping}
            onValueChange={(value) => onGroupingChange(value as ChartGrouping)}
          >
            <SelectTrigger className="h-8 w-[90px] rounded-lg corner-smooth-lg bg-card text-xs font-medium">
              <SelectValue placeholder="Grouping" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onCumulativeChange(!cumulative)}
            className="h-8 bg-card px-3 gap-2 text-xs font-medium"
          >
            <span>Cumulative</span>
            <Switch
              checked={cumulative}
              onCheckedChange={onCumulativeChange}
              className="scale-75"
            />
          </Button>
        </>
      )}

      {showChartToggle && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onViewModeChange(viewMode === "table" ? "chart" : "table")
              }
              className="h-8 bg-card px-3 gap-2 text-xs font-medium"
            >
              {viewMode === "table" ? (
                <>
                  <LineChart className="h-4 w-4" />
                  <span>View Chart</span>
                </>
              ) : (
                <>
                  <TableIcon className="h-4 w-4" />
                  <span>View Table</span>
                </>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {viewMode === "table"
                ? "Switch to time-series chart view"
                : "Switch to data table view"}
            </p>
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}
